import React, { useState, useRef, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import AppsShowcaseSchema from "../components/SEO/AppsShowcaseSchema";
import "../styles/apps-landing.css";

const APPS_DATA = {
  docno: {
    id: "docno",
    name: "DocNo – Notes, Expenses & Docs",
    shortName: "DocNo",
    category: "Productivity & Personal Finance",
    icon: "📝",
    themeClass: "theme-docno",
    accentColor: "#10b981",
    accentGlow: "rgba(16, 185, 129, 0.45)",
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.almondsoftwares.docno",
    badge: "100% Offline · Encrypted Vault · ₹10 Only",
    price: "₹10",
    priceTag: "₹10 Only (One-Time)",
    pricingDetail: "₹10 one-time purchase · Lifetime access · No subscriptions",
    ctaSubText: "GET IT FOR ₹10 ON",
    stickyActionText: "▶ Get DocNo (₹10)",
    headlineStart: "Notes, expenses & docs.",
    headlineGrad: "One fluid space.",
    tagline:
      "All-in-one productivity app by Almond Software. Capture rich notes, track daily expenses and budgets with visual analytics, and lock important documents in an encrypted vault. Zero cloud tracking.",
    pills: [
      "Offline & Encrypted",
      "Expense & Budget Analytics",
      "Markdown Notes",
      "Document Locker",
      "Receipt Scanner",
      "Financial Calculator"
    ],
    features: [
      {
        icon: "📝",
        title: "Smart Notes & Checklists",
        items: [
          "Rich markdown formatting & instant search",
          "Interactive checklist items with quick tags",
          "Audio voice memos & pin-to-top notes",
          "Organized folder notebooks with custom colors"
        ]
      },
      {
        icon: "💰",
        title: "Expense & Budget Tracker",
        items: [
          "Monthly budget targets with category breakdown",
          "Multi-currency support & visual spending rings",
          "Recurring bill reminders & transaction ledger",
          "CSV & PDF spending report exports"
        ]
      },
      {
        icon: "🗄️",
        title: "Secure Document Vault",
        items: [
          "Offline AES biometric & PIN file encryption",
          "Built-in camera receipt & document scanner",
          "Multi-page PDF generator with auto crop",
          "Zero telemetry — your files never leave device"
        ]
      },
      {
        icon: "🧮",
        title: "Financial Calculator & History",
        items: [
          "Built-in arithmetic calculator with tape history",
          "Instant paste from calculations into expense ledger",
          "Tax, tip & discount calculation shortcuts",
          "Quick expense splitting helper"
        ]
      },
      {
        icon: "🛡️",
        title: "Privacy-by-Design Architecture",
        items: [
          "100% offline local SQLite storage",
          "No required login or personal account creation",
          "No third-party trackers, no advertising SDKs",
          "Local backup & restore with password export"
        ]
      },
      {
        icon: "⚡",
        title: "Retina Performance Engine",
        items: [
          "Instant startup in under 200ms",
          "True black OLED dark mode & high-contrast mode",
          "Ultra-low memory and battery consumption",
          "Smooth liquid animations with haptic feedback"
        ]
      }
    ],
    screenshots: [
      {
        src: `${process.env.PUBLIC_URL}/docno-preview.jpg`,
        alt: "DocNo Notes, Expenses and Document Vault Android App Interface"
      },
      {
        src: `${process.env.PUBLIC_URL}/docno-expense.jpg`,
        alt: "DocNo Monthly Budget Chart, Category Breakdown and Transaction Ledger"
      }
    ]
  },

  forge: {
    id: "forge",
    name: "Forge - Photo Editor & Graphic Studio",
    shortName: "Forge",
    category: "Photography & Graphic Design",
    icon: "🎨",
    themeClass: "theme-forge",
    accentColor: "#a855f7",
    accentGlow: "rgba(168, 85, 247, 0.45)",
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.almondsoftwares.forge",
    badge: "Multi-Layer Engine · RGB Curves · AI Masking",
    price: "Free",
    priceTag: "Free Download",
    pricingDetail: "Free on Google Play · No intrusive ads",
    ctaSubText: "GET IT ON",
    stickyActionText: "▶ Install on Android Free",
    headlineStart: "Pro photo editing.",
    headlineGrad: "Desktop power in pocket.",
    tagline:
      "A powerhouse creative photo editing studio by Almond Software. Desktop-grade layer system with 24 blend modes, precision RGB curve grading, 3D LUTs, and on-device neural AI masking.",
    pills: [
      "Multi-Layer Editing",
      "24 Blend Modes",
      "RGB Curves & 3D LUTs",
      "AI Sky & Subject Masking",
      "Spot Healing & Dodge/Burn",
      "Lossless RAW Support"
    ],
    features: [
      {
        icon: "📑",
        title: "Full Layer System & 24 Blend Modes",
        items: [
          "Raster, shape, text, and fill layer support",
          "24 industry-standard blend modes (Screen, Multiply, Overlay)",
          "Non-destructive layer masks & opacity sliders",
          "Layer grouping, clipping masks & lock controls"
        ]
      },
      {
        icon: "📈",
        title: "RGB Curves & 3D LUT Grading",
        items: [
          "Precision interactive RGB & Luminance curves",
          "Full HSL selective color wheel adjustment",
          "Cinematic 3D LUT import (.cube) & presets",
          "Color balance, split toning & channel mixer"
        ]
      },
      {
        icon: "🤖",
        title: "On-Device Neural AI Masking",
        items: [
          "One-tap AI sky replacement & enhancement",
          "Instant subject isolation & background erase",
          "Edge-aware hair & boundary refinement brush",
          "Zero cloud upload — runs locally on your NPU"
        ]
      },
      {
        icon: "✨",
        title: "Pro Retouching & Restoration",
        items: [
          "Frequency separation for natural skin retouching",
          "Content-aware spot healing & clone stamp",
          "Dodge & burn brush with exposure targeting",
          "De-haze, chromatic aberration & lens correction"
        ]
      },
      {
        icon: "✍️",
        title: "Vector Typography & Layouts",
        items: [
          "Curved text, custom kerning & letter tracking",
          "Drop shadows, neon strokes & gradient fills",
          "Social canvas presets (Instagram, TikTok, YouTube)",
          "Rich library of vector shapes, badges & stickers"
        ]
      },
      {
        icon: "📷",
        title: "Pro Export & RAW Pipeline",
        items: [
          "16-bit color processing engine for smooth gradients",
          "DNG, CR2, NEF RAW file format decoding",
          "Lossless PNG, WebP, and full-resolution JPEG export",
          "Exif metadata inspection and preservation"
        ]
      }
    ],
    screenshots: [
      {
        src: `${process.env.PUBLIC_URL}/forge-preview.jpg`,
        alt: "Forge Photo Editor Multi-Layer and RGB Curves Workspace"
      },
      {
        src: `${process.env.PUBLIC_URL}/forge-tools.jpg`,
        alt: "Forge Photo Editor Studio Filters, Retouching and Split Screen Comparison"
      }
    ]
  },

  video: {
    id: "video",
    name: "Almond Video Player",
    shortName: "Video Player",
    category: "Video Players & Editors",
    icon: "🎬",
    themeClass: "theme-video",
    accentColor: "#ff5f2e",
    accentGlow: "rgba(255, 95, 46, 0.45)",
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.a.almondvideoplayer&pcampaignid=web_share",
    badge: "Hardware Accelerated · In-App Stream Downloader",
    price: "Free",
    priceTag: "Free Download",
    pricingDetail: "Free on Google Play · Ad-free playback",
    ctaSubText: "GET IT ON",
    stickyActionText: "▶ Install on Android Free",
    headlineStart: "Every video format.",
    headlineGrad: "Zero playback hassle.",
    tagline:
      "A high-octane, ad-free Android video player by Almond Software. Hardware-accelerated decoding, built-in browser with automatic video stream detection, intuitive swipe gestures, and subtitle sync.",
    pills: [
      "4K Ultra-HD Decoded",
      "Web Stream Downloader",
      "HLS & DASH Streaming",
      "Subtitle Auto-Sync",
      "Gesture Controls",
      "Picture-in-Picture"
    ],
    features: [
      {
        icon: "🎬",
        title: "Every Format, Zero Hassle",
        items: [
          "MP4, MKV, AVI, MOV, FLV, WEBM, 3GP, TS",
          "HLS (M3U8) and MPEG-DASH network streaming",
          "HDR10 and 10-bit video playback capability",
          "Multi-audio track switching & surround passthrough"
        ]
      },
      {
        icon: "🌐",
        title: "Built-In Browser & Stream Grabber",
        items: [
          "Browse any video site with integrated ad-blocker",
          "Automatic video stream resolution detection",
          "Direct one-tap download or streaming from web",
          "Background download manager with pause/resume"
        ]
      },
      {
        icon: "⚡",
        title: "Hardware Accelerated Engine",
        items: [
          "Native GPU decoding with low CPU overhead",
          "Smooth 60fps/120fps high-refresh rate playback",
          "Last position instant resume & playback speed 0.25x-4x",
          "A-B repeat loop & background audio playback"
        ]
      },
      {
        icon: "💬",
        title: "Subtitles Done Right",
        items: [
          "External SRT, VTT, SSA, ASS subtitle support",
          "Sub-second sync delay & timing offset adjustment",
          "Custom font sizes, text color, outline & background",
          "Online subtitle search and auto-download"
        ]
      },
      {
        icon: "📱",
        title: "Fluid Gesture Controls",
        items: [
          "Left swipe for brightness, right swipe for volume",
          "Horizontal swipe for smooth seeking with thumbnail preview",
          "Double-tap to skip forward or backward",
          "Picture-in-picture (PiP) floating window mode"
        ]
      },
      {
        icon: "📁",
        title: "Smart Media Library",
        items: [
          "Auto-scanning of internal & SD card storage",
          "Recently watched view & folder structure navigation",
          "Private folder lock with PIN protection",
          "Bulk video rename, delete, and share tools"
        ]
      }
    ],
    screenshots: [1, 2, 3, 4, 5, 6].map((n) => ({
      src: `${process.env.PUBLIC_URL}/avp1 (${n}).jpeg`,
      alt: `Almond Video Player screenshot ${n} with ultra HD playback and streaming controls`
    }))
  },

  music: {
    id: "music",
    name: "Almond Music Player",
    shortName: "Music Player",
    category: "Music & Audio",
    icon: "🎵",
    themeClass: "theme-music",
    accentColor: "#00e5c7",
    accentGlow: "rgba(0, 229, 199, 0.45)",
    playStoreUrl: "https://play.google.com/store/apps/details?id=com.a.almondmusicplayer&pcampaignid=web_share",
    badge: "Studio 64-Band EQ · Hi-Res DSP · Gapless",
    price: "Free",
    priceTag: "Free Download",
    pricingDetail: "Free on Google Play · Studio DSP offline",
    ctaSubText: "GET IT ON",
    stickyActionText: "▶ Install on Android Free",
    headlineStart: "Studio-grade sound.",
    headlineGrad: "Audiophile precision.",
    tagline:
      "Hi-res offline audio player with an unmatched professional DSP engine by Almond Software. 64-band equalizer, 24-band parametric EQ, real-time spectrum analyzer, and offline privacy.",
    pills: [
      "64-Band Graphic EQ",
      "24-Band Parametric EQ",
      "Real-Time Spectrum Analyzer",
      "Gapless Playback",
      "Bass Boost & 3D Surround",
      "100% Offline & Private"
    ],
    features: [
      {
        icon: "🎚️",
        title: "Studio-Grade DSP Audio",
        items: [
          "64-band graphic equalizer with high Q-factor",
          "24-band parametric equalizer for surgeon-like tuning",
          "Dynamic multiband compression & limiter",
          "Bit-perfect 24-bit/192kHz audio processing"
        ]
      },
      {
        icon: "📊",
        title: "Real-Time Spectrum Analyzer",
        items: [
          "60fps high-resolution FFT frequency analyzer",
          "LUFS loudness meter & true peak level monitor",
          "Live BPM beat detection & key visualizer",
          "Custom visualizer styles (bars, wave, circular)"
        ]
      },
      {
        icon: "🎧",
        title: "Smart Acoustic Sound Profiles",
        items: [
          "Deep Bass Boost, 3D Spatial Audio, Clear Vocalizer",
          "Rock, Jazz, Classical, EDM, Pop, Acoustic presets",
          "Per-headphone & per-device custom EQ profiles",
          "Left/Right channel balance control"
        ]
      },
      {
        icon: "🎨",
        title: "Dynamic Liquid Now-Playing",
        items: [
          "Real-time color extraction from album artwork",
          "Fluid wave visualizer responding to bass frequencies",
          "LRC synced lyrics with karaoke auto-scroll",
          "OLED pure black theme & liquid blur glass cards"
        ]
      },
      {
        icon: "🔋",
        title: "High Performance Engine",
        items: [
          "Low CPU consumption native C++ audio engine",
          "Gapless crossfade playback & replay gain volume match",
          "Sleep timer, lock-screen controls & Android Auto support",
          "FLAC, ALAC, WAV, APE, MP3, AAC, OGG, Opus decoded"
        ]
      },
      {
        icon: "🔒",
        title: "Your Music, Your Device",
        items: [
          "Fully offline playback with zero internet dependency",
          "Local audio metadata tag editor & album art grabber",
          "No account required, no tracking, no ads",
          "Smart playlist generator (most played, recently added)"
        ]
      }
    ],
    screenshots: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => ({
      src: `${process.env.PUBLIC_URL}/amp (${n}).jpeg`,
      alt: `Almond Music Player screenshot ${n} with 64-band equalizer and hi-res now playing screen`
    }))
  }
};

/**
 * Scroll reveal component with IntersectionObserver
 */
function Reveal({ children, className = "", delay = 0, as: Tag = "div" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`apps-reveal${visible ? " is-visible" : ""}${className ? ` ${className}` : ""}`}
      style={{ transitionDelay: visible ? `${delay}s` : "0s" }}
    >
      {children}
    </Tag>
  );
}

export default function AppsLanding() {
  const getInitialApp = () => {
    if (typeof window !== "undefined" && window.location.hash) {
      const hash = window.location.hash.replace("#", "").toLowerCase();
      if (APPS_DATA[hash]) return hash;
    }
    return "docno";
  };

  const [activeKey, setActiveKey] = useState(getInitialApp);
  const app = useMemo(() => APPS_DATA[activeKey] || APPS_DATA.docno, [activeKey]);

  const switchApp = (key) => {
    setActiveKey(key);
    if (typeof window !== "undefined") {
      window.history.replaceState(null, null, `#${key}`);
    }
  };

  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.replace("#", "").toLowerCase();
      if (APPS_DATA[hash]) setActiveKey(hash);
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const [lightbox, setLightbox] = useState(null); // { images, index }
  const [closing, setClosing] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  // Interactive Live Widget Demo State for each app
  const [demoExpense, setDemoExpense] = useState(1450);
  const [demoFilter, setDemoFilter] = useState("cinematic");
  const [demoEqGain, setDemoEqGain] = useState(6);

  const pageRef = useRef(null);
  const flipStageRef = useRef(null);
  const canHover = useRef(false);
  const reducedMotion = useRef(false);

  useEffect(() => {
    canHover.current = window.matchMedia("(pointer: fine)").matches;
    reducedMotion.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openLightbox = (images, index) => {
    setClosing(false);
    setLightbox({ images, index });
  };

  const closeLightbox = () => {
    setClosing(true);
    window.setTimeout(() => {
      setLightbox(null);
      setClosing(false);
    }, 280);
  };

  const stepLightbox = (delta) => {
    setLightbox((prev) => {
      if (!prev) return prev;
      const total = prev.images.length;
      const nextIndex = (prev.index + delta + total) % total;
      return { ...prev, index: nextIndex };
    });
  };

  useEffect(() => {
    if (!lightbox) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") stepLightbox(1);
      if (e.key === "ArrowLeft") stepLightbox(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  // Ambient spotlight following cursor
  const handlePageMove = (e) => {
    if (!canHover.current || reducedMotion.current) return;
    const el = pageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--spot-x", `${((e.clientX - rect.left) / rect.width) * 100}%`);
    el.style.setProperty("--spot-y", `${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  // 3D tilt on card stage
  const handleStageMove = (e) => {
    if (!canHover.current || reducedMotion.current) return;
    const el = flipStageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    el.style.setProperty("--tiltX", `${(0.5 - py) * 9}deg`);
    el.style.setProperty("--tiltY", `${(px - 0.5) * 13}deg`);
    el.style.setProperty("--glareX", `${px * 100}%`);
    el.style.setProperty("--glareY", `${py * 100}%`);
  };

  const handleStageLeave = () => {
    const el = flipStageRef.current;
    if (!el) return;
    el.style.setProperty("--tiltX", "0deg");
    el.style.setProperty("--tiltY", "0deg");
  };

  const toggleFaq = (idx) => {
    setActiveFaq((prev) => (prev === idx ? null : idx));
  };

  const faqs = [
    {
      q: "What is the pricing for Almond Software Android apps?",
      a: "DocNo is available on Google Play for just ₹10 (a one-time payment for lifetime access with no monthly subscriptions and zero ads). Forge Photo Editor, Almond Video Player, and Almond Music Player are completely free to download directly from the Google Play Store."
    },
    {
      q: "Is my personal data and financial notes safe in DocNo?",
      a: "Absolutely. DocNo is engineered with a strict 100% offline, privacy-first architecture. All your notes, checklist items, receipt scans, document vault files, and expense budgets stay encrypted on your local Android device storage. Nothing is transmitted to external servers."
    },
    {
      q: "Can Forge Photo Editor handle layers, blending modes, and RAW files?",
      a: "Yes. Forge features a desktop-grade multi-layer engine supporting raster, adjustment, shape, and text layers with 24 professional blend modes (Multiply, Screen, Overlay, Soft Light, etc.), RGB curves, and lossless RAW image decoding."
    },
    {
      q: "Does Almond Video Player support downloading streams and subtitle adjustment?",
      a: "Yes. Almond Video Player includes an integrated web browser that automatically detects video streams on websites for one-tap downloading, along with sub-second subtitle synchronization and hardware-accelerated 4K/MKV playback."
    },
    {
      q: "What makes Almond Music Player different from standard music apps?",
      a: "Almond Music Player is built for audiophiles with an industry-leading 64-band graphic equalizer, 24-band parametric EQ, live FFT spectrum analyzer, gapless playback, and a hi-fi native C++ DSP engine operating completely offline."
    }
  ];

  return (
    <div
      className={`apps-page ${app.themeClass}`}
      ref={pageRef}
      onMouseMove={handlePageMove}
    >
      <Helmet>
        <title>{`${app.name} (${app.price === "Free" ? "Free Android App" : "₹10 Android App"}) | Almond Software`}</title>
        <meta
          name="description"
          content={`Download ${app.name} on Android: ${app.tagline} ${app.pricingDetail}. Verified official app on Google Play.`}
        />
        <link rel="canonical" href={`https://almondsoftwares.in/apps#${activeKey}`} />
        <meta
          name="keywords"
          content={`${app.name}, ${app.shortName} android app, ${app.category}, ${app.pills.join(", ")}, almond software apps`}
        />
        <meta
          property="og:title"
          content={`${app.name} | Official Android App by Almond Software`}
        />
        <meta
          property="og:description"
          content={`Download ${app.name} on Android: ${app.tagline} ${app.pricingDetail}.`}
        />
        <meta
          property="og:image"
          content={app.screenshots[0].src.startsWith("http") ? app.screenshots[0].src : `https://almondsoftwares.in${app.screenshots[0].src}`}
        />
        <meta property="og:url" content={`https://almondsoftwares.in/apps#${activeKey}`} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${app.name} | Android App`} />
        <meta
          name="twitter:description"
          content={`Download ${app.name} on Google Play. ${app.pricingDetail}.`}
        />
        <meta
          name="twitter:image"
          content={app.screenshots[0].src.startsWith("http") ? app.screenshots[0].src : `https://almondsoftwares.in${app.screenshots[0].src}`}
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
      </Helmet>

      {/* Structured SEO Schema */}
      <AppsShowcaseSchema />

      {/* Liquid Caustic Ambient Glows */}
      <div className="apps-glow apps-glow-docno" />
      <div className="apps-glow apps-glow-forge" />
      <div className="apps-glow apps-glow-video" />
      <div className="apps-glow apps-glow-music" />
      <div className="apps-spotlight" />
      <div className="apps-noise" />

      {/* Floating Liquid Background Blobs */}
      <div className="liquid-canvas" aria-hidden="true">
        <div className="liquid-blob blob-1" />
        <div className="liquid-blob blob-2" />
        <div className="liquid-blob blob-3" />
      </div>

      <div className="apps-container">
        {/* Navigation Bar Header */}
        <div className={`apps-topbar${scrolled ? " is-scrolled" : ""}`}>
          <Link to="/" className="apps-brand">
            <span className="apps-brand-gem">◆</span>
            <span>Almond Software Apps</span>
          </Link>
          <div className="apps-top-actions">
            <a
              href={app.playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="apps-top-play-btn"
            >
              <span>Google Play</span>
              <span className="play-arrow">↗</span>
            </a>
            <Link to="/" className="apps-back">
              ← Main Site
            </Link>
          </div>
        </div>

        {/* Hero Section */}
        <div className="apps-hero">
          <div className="apps-eyebrow apps-fade-in" style={{ animationDelay: "0.05s" }}>
            <span className="pulse-dot"></span>
            <span>Official Android Apps · Google Play · Built by Almond Software</span>
          </div>

          <h1 className="apps-fade-in" style={{ animationDelay: "0.15s" }}>
            {app.headlineStart}{" "}
            <span className="grad-app-title">
              {app.headlineGrad}
            </span>
          </h1>

          <p className="apps-hero-lead apps-fade-in" style={{ animationDelay: "0.26s" }}>
            Explore Almond Software's flagship Android application suite. Crafted with retina liquid morphism aesthetics, high performance, and user privacy at their core.
          </p>
        </div>

        {/* Verified Google Play Developer Profile Banner */}
        <div className="apps-dev-banner-wrap apps-fade-in" style={{ animationDelay: "0.3s" }}>
          <div className="apps-dev-banner">
            <div className="dev-banner-left">
              <span className="dev-play-badge">▶ GOOGLE PLAY</span>
              <div className="dev-banner-meta">
                <strong>Almond Softwares Developer Catalog</strong>
                <span>Official publisher of DocNo, Forge, and Android Media Players</span>
              </div>
            </div>
            <a
              href="https://play.google.com/store/apps/developer?id=ALMOND+SOFTWARES"
              target="_blank"
              rel="noopener noreferrer"
              className="dev-visit-btn"
            >
              <span>Explore Developer Profile</span>
              <span className="dev-arrow">↗</span>
            </a>
          </div>
        </div>

        {/* 4-App Segmented Liquid Pill Switcher */}
        <div className="apps-switch-wrapper apps-fade-in" style={{ animationDelay: "0.36s" }}>
          <div className="apps-segmented-control" role="tablist" aria-label="Select an Android application">
            <button
              type="button"
              className={`app-seg-btn ${activeKey === "docno" ? "active" : ""}`}
              role="tab"
              aria-selected={activeKey === "docno"}
              onClick={() => switchApp("docno")}
              id="tab-docno"
            >
              <span className="app-seg-icon">📝</span>
              <span className="app-seg-name">DocNo</span>
              <span className="app-seg-pill">₹10 · Notes & Docs</span>
            </button>

            <button
              type="button"
              className={`app-seg-btn ${activeKey === "forge" ? "active" : ""}`}
              role="tab"
              aria-selected={activeKey === "forge"}
              onClick={() => switchApp("forge")}
              id="tab-forge"
            >
              <span className="app-seg-icon">🎨</span>
              <span className="app-seg-name">Forge</span>
              <span className="app-seg-pill">Free · Photo Studio</span>
            </button>

            <button
              type="button"
              className={`app-seg-btn ${activeKey === "video" ? "active" : ""}`}
              role="tab"
              aria-selected={activeKey === "video"}
              onClick={() => switchApp("video")}
              id="tab-video"
            >
              <span className="app-seg-icon">🎬</span>
              <span className="app-seg-name">Video Player</span>
              <span className="app-seg-pill">Free · 4K & Stream</span>
            </button>

            <button
              type="button"
              className={`app-seg-btn ${activeKey === "music" ? "active" : ""}`}
              role="tab"
              aria-selected={activeKey === "music"}
              onClick={() => switchApp("music")}
              id="tab-music"
            >
              <span className="app-seg-icon">🎵</span>
              <span className="app-seg-name">Music Player</span>
              <span className="app-seg-pill">Free · 64-Band EQ</span>
            </button>
          </div>
        </div>

        {/* Retina 3D Device Showcase Stage */}
        <div
          className="apps-stage-wrap apps-fade-in"
          style={{ animationDelay: "0.45s" }}
          ref={flipStageRef}
          onMouseMove={handleStageMove}
          onMouseLeave={handleStageLeave}
        >
          <div className="liquid-stage-card">
            <div className="stage-card-shimmer" />
            <div className="stage-content-grid">
              {/* Left Column: App Information & CTA */}
              <div className="stage-info-col">
                <div className="app-category-badge">
                  <span>{app.icon}</span>
                  <span>{app.category}</span>
                  <span className="badge-divider">•</span>
                  <span className="badge-highlight">{app.badge}</span>
                </div>

                <h2 className="app-hero-title">{app.name}</h2>
                <p className="app-hero-tagline">{app.tagline}</p>

                {/* Quick Feature Bullet Highlights */}
                <div className="stage-quick-pills">
                  {app.pills.map((pill) => (
                    <span key={pill} className="stage-pill">
                      <span className="stage-pill-dot">✓</span>
                      {pill}
                    </span>
                  ))}
                </div>

                <div className="stage-actions">
                  <a
                    href={app.playStoreUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="app-primary-cta"
                  >
                    <span className="play-logo-icon">▶</span>
                    <div className="cta-text-wrap">
                      <span className="cta-sub">{app.ctaSubText}</span>
                      <span className="cta-main">Google Play</span>
                    </div>
                  </a>

                  <button
                    type="button"
                    className="app-secondary-btn"
                    onClick={() => {
                      const el = document.getElementById("screenshots-section");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                  >
                    View Screenshots ({app.screenshots.length})
                  </button>
                </div>

                <div className="stage-trust-row">
                  <span className="trust-item">🛡️ Verified by Play Protect</span>
                  <span className="trust-item">🏷️ {app.pricingDetail}</span>
                  <span className="trust-item">🔒 Zero Invasive Ads</span>
                </div>
              </div>

              {/* Right Column: Retina Liquid Interactive Device Mockup */}
              <div className="stage-mockup-col">
                <div className="retina-device-frame">
                  <div className="device-specular-glare" />
                  <div className="device-notch">
                    <span className="speaker-slot"></span>
                    <span className="camera-lens"></span>
                  </div>

                  <div
                    className="device-screen-container"
                    onClick={() => openLightbox(app.screenshots, 0)}
                    title="Click to view full screen"
                  >
                    <img
                      src={app.screenshots[0].src}
                      alt={app.screenshots[0].alt}
                      className="device-screenshot-img"
                    />
                    <div className="device-screen-overlay">
                      <span className="zoom-hint">⤢ Tap to Expand</span>
                    </div>
                  </div>

                  <div className="device-home-indicator"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Interactive Widget Demo Section */}
        <section className="apps-interactive-demo-section">
          <Reveal as="div" className="interactive-demo-card">
            <div className="demo-header">
              <span className="demo-pill">LIVE INTERACTIVE PREVIEW</span>
              <h3>Experience {app.shortName} in Your Browser</h3>
              <p>Test real-time features before downloading onto your phone</p>
            </div>

            {activeKey === "docno" && (
              <div className="demo-body demo-docno">
                <div className="demo-slider-group">
                  <label htmlFor="budget-slider">
                    Adjust Monthly Expense Target: <strong>${demoExpense} / $2,500</strong>
                  </label>
                  <input
                    id="budget-slider"
                    type="range"
                    min="300"
                    max="2500"
                    step="50"
                    value={demoExpense}
                    onChange={(e) => setDemoExpense(Number(e.target.value))}
                  />
                </div>
                <div className="demo-docno-grid">
                  <div className="demo-metric-box">
                    <span className="demo-box-label">Monthly Spent</span>
                    <span className="demo-box-value">${demoExpense}.00</span>
                    <div className="demo-progress-bar">
                      <div
                        className="demo-progress-fill"
                        style={{ width: `${Math.min(100, (demoExpense / 2500) * 100)}%` }}
                      />
                    </div>
                  </div>
                  <div className="demo-metric-box">
                    <span className="demo-box-label">Remaining Safe Budget</span>
                    <span className="demo-box-value">${Math.max(0, 2500 - demoExpense)}.00</span>
                    <span className="demo-box-tag">Safe Zone: Healthy</span>
                  </div>
                  <div className="demo-metric-box">
                    <span className="demo-box-label">Encrypted Docs Stored</span>
                    <span className="demo-box-value">12 Files</span>
                    <span className="demo-box-tag">Biometric AES-256</span>
                  </div>
                </div>
              </div>
            )}

            {activeKey === "forge" && (
              <div className="demo-body demo-forge">
                <div className="demo-filter-tabs">
                  {["cinematic", "cyberpunk", "black-white", "vivid-hdr"].map((filter) => (
                    <button
                      key={filter}
                      type="button"
                      className={`filter-btn ${demoFilter === filter ? "active" : ""}`}
                      onClick={() => setDemoFilter(filter)}
                    >
                      {filter.replace("-", " ").toUpperCase()}
                    </button>
                  ))}
                </div>
                <div className={`demo-photo-preview filter-${demoFilter}`}>
                  <div className="filter-badge">Preset: {demoFilter} (24 Blend Modes Active)</div>
                  <div className="filter-curve-indicator">RGB Tone Curve: Dynamic S-Curve Applied</div>
                </div>
              </div>
            )}

            {activeKey === "video" && (
              <div className="demo-body demo-video">
                <div className="codec-grid">
                  {["4K MKV 60fps", "H.265 / HEVC", "HLS Streaming (.m3u8)", "DASH (.mpd)", "Hardware Decode", "SRT Subtitles"].map(
                    (codec) => (
                      <div key={codec} className="codec-pill">
                        <span className="codec-dot">●</span>
                        <span>{codec}</span>
                        <span className="codec-badge">Supported</span>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {activeKey === "music" && (
              <div className="demo-body demo-music">
                <div className="demo-eq-sliders">
                  {[-6, 0, 4, 8, 5, 2, -1, 3, 7, 4].map((db, i) => (
                    <div key={i} className="eq-band-col">
                      <div
                        className="eq-band-fill"
                        style={{ height: `${Math.min(100, Math.max(10, 50 + db * 5 + demoEqGain * 3))}%` }}
                      />
                      <span className="eq-freq">{(i + 1) * 2}kHz</span>
                    </div>
                  ))}
                </div>
                <div className="demo-slider-group" style={{ marginTop: "16px" }}>
                  <label htmlFor="bass-slider">
                    Studio Bass Boost: <strong>+{demoEqGain} dB</strong>
                  </label>
                  <input
                    id="bass-slider"
                    type="range"
                    min="0"
                    max="12"
                    step="1"
                    value={demoEqGain}
                    onChange={(e) => setDemoEqGain(Number(e.target.value))}
                  />
                </div>
              </div>
            )}
          </Reveal>
        </section>

        {/* Feature Cards Grid */}
        <section className="apps-features-section">
          <div className="section-header-centered">
            <span className="section-pill">ARCHITECTURE & CAPABILITIES</span>
            <h2 className="apps-section-title">
              What Makes {app.shortName} Extraordinary
            </h2>
            <p className="apps-section-sub">
              Every detail engineered from scratch for buttery responsiveness and native Android performance.
            </p>
          </div>

          <div className="apps-feature-grid" key={activeKey}>
            {app.features.map((f, i) => (
              <Reveal as="div" className="apps-feature-card" delay={i * 0.06} key={f.title}>
                <div className="apps-fi">{f.icon}</div>
                <h3>{f.title}</h3>
                <ul>
                  {f.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Screenshot Gallery Section */}
        <section className="apps-gallery-wrap" id="screenshots-section">
          <div className="section-header-centered">
            <span className="section-pill">RETINA INTERFACE</span>
            <h2 className="apps-section-title">Inside the {app.shortName} Interface</h2>
            <p className="apps-gallery-hint">
              Tap any high-definition screen to inspect it in full retina lightbox mode
            </p>
          </div>

          <div className="apps-gallery-scroll">
            {app.screenshots.map((shot, i) => (
              <Reveal as="div" className="apps-phone-frame" delay={i * 0.05} key={shot.src}>
                <div
                  className="apps-phone-tap"
                  onClick={() => openLightbox(app.screenshots, i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") openLightbox(app.screenshots, i);
                  }}
                  aria-label={`Open screenshot ${i + 1} full size`}
                >
                  <div className="apps-phone-notch" />
                  <div className="apps-phone-screen">
                    <img src={shot.src} alt={shot.alt} loading="lazy" />
                    <div className="apps-phone-glass" />
                    <div className="apps-phone-zoom">
                      <span>⤢</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* All 4 Apps Quick Matrix / Comparison Cards */}
        <section className="apps-suite-overview">
          <div className="section-header-centered">
            <span className="section-pill">COMPLETE ECOSYSTEM</span>
            <h2 className="apps-section-title">The Almond Software Mobile Suite</h2>
            <p className="apps-section-sub">
              Four dedicated powerhouses designed to elevate productivity, creativity, and entertainment.
            </p>
          </div>

          <div className="suite-cards-grid">
            {Object.keys(APPS_DATA).map((key) => {
              const item = APPS_DATA[key];
              const isCurrent = key === activeKey;
              return (
                <div
                  key={key}
                  className={`suite-card ${isCurrent ? "is-active" : ""}`}
                  onClick={() => {
                    switchApp(key);
                    window.scrollTo({ top: 320, behavior: "smooth" });
                  }}
                >
                  <div className="suite-card-top">
                    <span className="suite-icon">{item.icon}</span>
                    <span className="suite-badge">{item.badge.split("·")[0]}</span>
                  </div>
                  <h4>{item.shortName}</h4>
                  <p className="suite-cat">{item.category}</p>
                  <p className="suite-desc">{item.tagline.slice(0, 110)}...</p>
                  <div className="suite-footer">
                    <span className="suite-select-btn">
                      {isCurrent ? "Currently Viewing" : "Switch App →"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* SEO FAQ Accordion Section */}
        <section className="apps-faq-section">
          <div className="section-header-centered">
            <span className="section-pill">COMMON QUESTIONS</span>
            <h2 className="apps-section-title">Frequently Asked Questions</h2>
            <p className="apps-section-sub">
              Everything you need to know about Almond Software's Android applications.
            </p>
          </div>

          <div className="faq-accordion-list">
            {faqs.map((faq, idx) => {
              const isOpen = activeFaq === idx;
              return (
                <div
                  key={faq.q}
                  className={`faq-item ${isOpen ? "open" : ""}`}
                  onClick={() => toggleFaq(idx)}
                >
                  <button
                    type="button"
                    className="faq-question-btn"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <span className="faq-toggle-icon">{isOpen ? "−" : "+"}</span>
                  </button>
                  {isOpen && (
                    <div className="faq-answer-content">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Lightbox Modal */}
      {lightbox && (
        <div
          className={`apps-lightbox-backdrop${closing ? " closing" : ""}`}
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="apps-lightbox-nav prev"
            aria-label="Previous screenshot"
            onClick={(e) => {
              e.stopPropagation();
              stepLightbox(-1);
            }}
          >
            ‹
          </button>

          <div className="apps-lightbox-stage" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="apps-lightbox-close"
              aria-label="Close lightbox"
              onClick={closeLightbox}
            >
              ×
            </button>
            <img
              src={lightbox.images[lightbox.index].src}
              alt={lightbox.images[lightbox.index].alt}
              onClick={closeLightbox}
            />
            <div className="apps-lightbox-counter">
              {lightbox.index + 1} / {lightbox.images.length}
            </div>
          </div>

          <button
            type="button"
            className="apps-lightbox-nav next"
            aria-label="Next screenshot"
            onClick={(e) => {
              e.stopPropagation();
              stepLightbox(1);
            }}
          >
            ›
          </button>
        </div>
      )}

      {/* Sticky Bottom Liquid CTA Bar */}
      <div className="apps-sticky-cta">
        <div className="sticky-cta-content">
          <div className="sticky-app-info">
            <span className="sticky-app-icon">{app.icon}</span>
            <div className="sticky-text-wrap">
              <strong>{app.name}</strong>
              <span>{app.pricingDetail}</span>
            </div>
          </div>
          <a
            href={app.playStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="apps-store-btn"
          >
            {app.stickyActionText}
          </a>
        </div>
      </div>
    </div>
  );
}